import './style.css';
import { subscribe, getState, setState, showToast } from './store.js';
import {
  goLock, checkWorkerConnectivity, ensureFxRates, setOnline, checkWebauthnAvailable,
  cancelOpenAccount, cancelAddRecipient, cancelImportKey, cancelConfirmReveal, closeStatusPanel,
  closeProfileMenu, closeLicense, closeLanguagePicker, setLanguage, consumeScreenHash, go, goReceive, goSend,
  autoLockMsFor, isWebauthnPromptActive, skipVerifyBackup
} from './actions.js';
import { render } from './app.js';
import { initSwUpdateWatcher } from './sw-update.js';
import { t } from './i18n/index.js';

const root = document.getElementById('app');

// Reads a `#screen=X` fragment (set by a desktop "open in new window"
// click — actions.js's openInNewWindow) before the first render, so
// store.js's pendingScreen is already in place the moment the Landing
// unlock flow completes.
consumeScreenHash();

subscribe(() => render(root));
render(root);

// English is the only catalog bundled synchronously (src/i18n/index.js),
// so a device that persisted a non-English choice still boots and paints
// its very first frame in English — this loads and swaps in the real
// catalog as soon as its chunk resolves, triggering one follow-up
// re-render. A brief English flash on cold load is an accepted trade-off
// for not shipping 22 languages of copy in the initial bundle.
if (getState().language !== 'en') setLanguage(getState().language);

// ---- Modal focus trap + Escape-to-close --------------------------------
// A single delegated listener, added once, rather than per-modal-render —
// the app fully rebuilds the DOM on every state change, so anything bound
// per-render would need explicit cleanup to avoid leaking duplicates.
function openModalCancel(state) {
  if (state.showOpenAccount) return cancelOpenAccount;
  if (state.showAddRecipient) return cancelAddRecipient;
  if (state.showImportKey) return cancelImportKey;
  if (state.showConfirmReveal) return cancelConfirmReveal;
  if (state.showStatusPanel) return closeStatusPanel;
  if (state.showProfileMenu) return closeProfileMenu;
  if (state.showLicense) return closeLicense;
  if (state.showLanguagePicker) return closeLanguagePicker;
  // Escape acts as this modal's "Skip for now" -- consistent with the
  // ceremony being skippable everywhere else, not a special case here.
  if (state.showVerifyBackup) return skipVerifyBackup;
  return null;
}

document.addEventListener('keydown', (e) => {
  const cancel = openModalCancel(getState());
  if (!cancel) return;

  if (e.key === 'Escape') {
    e.preventDefault();
    cancel();
    return;
  }

  if (e.key === 'Tab') {
    const modalCard = document.querySelector('.modal-card');
    if (!modalCard) return;
    const focusables = modalCard.querySelectorAll('button, input, select, textarea, a[href]');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
});

// ---- Desktop keyboard shortcuts (g h / g r / g s) ----------------------
// feature-ui.md's Desktop section: "Hover states + keyboard shortcuts...
// for power users" — a chorded 'g' prefix, matching the same convention
// GitHub/Gmail/Linear use, rather than bare single-key shortcuts that
// would collide with normal typing anywhere a plain <input> has focus.
// Gated to the desktop breakpoint (matchMedia, matching style.css's own
// 900px) so it's inert on mobile/tablet, and to no modal + no text field
// focused so it never hijacks real typing.
const DESKTOP_MQ = '(min-width: 900px)';
const SHORTCUT_TARGETS = { h: () => go('home'), r: goReceive, s: goSend };
let chordArmed = false;
let chordTimer = null;

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || el.isContentEditable;
}

document.addEventListener('keydown', (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (!window.matchMedia(DESKTOP_MQ).matches) return;
  if (openModalCancel(getState())) return;
  if (isTypingTarget(document.activeElement)) return;

  if (chordArmed) {
    chordArmed = false;
    clearTimeout(chordTimer);
    const action = SHORTCUT_TARGETS[e.key];
    if (action) { e.preventDefault(); action(); }
    return;
  }
  if (e.key === 'g') {
    chordArmed = true;
    clearTimeout(chordTimer);
    // Un-arms itself if no second key follows soon enough — otherwise an
    // unrelated later 'h'/'r'/'s' keystroke (typed well after the 'g',
    // once a text field happens to be focused again) could misfire.
    chordTimer = setTimeout(() => { chordArmed = false; }, 1000);
  }
});

// Real key material only ever loads post-unlock now (loadWalletForSession
// in actions.js, called from unlock/unlockWithWebauthn/submitOpenAccount) —
// this boot-time check touches no key material, just confirms the worker
// itself is alive (tether.worker.js's ping handler) for the Status panel.
checkWorkerConnectivity();
checkWebauthnAvailable();
// Public FX data, no key material or unlocked profile needed — fetched as
// early as possible rather than gated on unlock (feature-backend.md item
// 7). Not awaited: same no-retry-ceiling reasoning as every other worker
// call now (see DECISIONS.md) — state.fxRates' localStorage-cached value
// (store.js's loadFxRates) already covers the UI until this resolves.
ensureFxRates();

window.addEventListener('online', () => setOnline(true));
window.addEventListener('offline', () => setOnline(false));

initSwUpdateWatcher();

// Auto-lock after inactivity and clear sensitive in-memory state (CLAUDE.md,
// feature-harden.md 6.2), with a warning before it fires so it doesn't cut
// off an in-progress flow. Timeout is configurable in Settings (default 5
// min) — read fresh from state each time armTimers runs, not cached at
// module load, so a mid-session Settings change takes effect on the very
// next reset instead of only after a reload.
const LOCK_WARNING_MS = 30 * 1000;
let lockTimer = null;
let warningTimer = null;
// Showing the warning toast is itself a state change, which would
// otherwise re-trigger resetLockTimer via the subscribe below and reset
// the very timer it's warning about. Suppress that one reentrant call.
let suppressReset = false;

function armTimers() {
  clearTimeout(lockTimer);
  clearTimeout(warningTimer);
  const s = getState();
  if (s.screen === 'landing') return;

  const autoLockMs = autoLockMsFor(s.autoLockMinutes);
  warningTimer = setTimeout(() => {
    const cur = getState();
    if (cur.screen === 'landing') return;
    suppressReset = true;
    showToast(t('toast.lockWarning'));
    // showToast (store.js) doesn't just set the toast synchronously -- it
    // also clears it via its own setState roughly 2200ms later. That clear
    // is a store change too, and subscribe(resetLockTimer) below treats
    // every store change as real activity, so ending suppression
    // synchronously right after the call above left that later clear free
    // to silently re-arm the whole countdown before the real lockTimer
    // below ever got to fire -- the wallet would warn, then quietly reset
    // itself, forever, and never actually lock (caught live via UAT,
    // 2026-08-13: no lock after 2x+ the configured timeout). Hold
    // suppression a bit past showToast's own clear delay instead.
    setTimeout(() => { suppressReset = false; }, 2300);
  }, Math.max(autoLockMs - LOCK_WARNING_MS, 0));

  lockTimer = setTimeout(() => {
    const cur = getState();
    if (cur.screen !== 'landing') goLock();
  }, autoLockMs);
}

function resetLockTimer() {
  if (suppressReset) return;
  armTimers();
}

['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
  document.addEventListener(evt, resetLockTimer, { passive: true })
);
subscribe(resetLockTimer);
resetLockTimer();

// Immediate lock on tab/app backgrounding (feature-harden.md 6.2), not just
// the inactivity timeout above — a wallet left open in a background tab is
// a real exposure window the timer alone doesn't close (it could be
// minutes before it fires). goLock() already blanks any revealed mnemonic
// words as part of its existing privateKeys reset (src/actions.js), which
// is also what 6.10 (blur mnemonic display on visibility loss) asks for —
// one listener, both requirements, per the doc's own suggestion. Only
// hidden->lock; becoming visible again doesn't need a matching branch,
// since staying visible just lets the normal inactivity timer keep running.
//
// isWebauthnPromptActive() guard is load-bearing, not defensive polish:
// navigator.credentials.create()/get() shows a native platform-auth
// prompt (Touch ID sheet, passkey dialog, iOS system sheet), and on
// several real platforms that prompt fires visibilitychange with
// document.hidden === true while it's up. Without this check, unlocking
// or re-authenticating via biometrics would call goLock() on itself
// mid-prompt, tearing down the session the WebAuthn call is about to
// write into the moment it resolves — a lock-yourself-out bug on exactly
// the platforms where biometrics are the primary unlock path (see
// landing.js's renderWelcomeBack, 6.15). See DECISIONS.md.
document.addEventListener('visibilitychange', () => {
  if (document.hidden && getState().screen !== 'landing' && !isWebauthnPromptActive()) goLock();
});

// feature-harden.md 6.1: ticks a re-render once/sec while the lock screen
// has a lockoutUntil on record, so landing.js's "Try again in Ns" button
// label/error actually counts down AND the button re-enables itself the
// moment the lockout expires — without this, both would sit frozen at
// whatever number the last failed attempt computed until some unrelated
// state change happened to re-render. setState({}) is a deliberate no-op
// patch: it exists only to produce a new state object reference so the
// normal subscribe(render) pipeline fires: no dedicated "just re-render"
// primitive exists in store.js, and adding one for a single call site
// isn't worth a new API.
//
// Deliberately does NOT stop ticking the instant lockoutUntil is in the
// past — a first version did (`lockoutUntil > Date.now()`), which was
// exactly backwards: it kept re-rendering right up until the lockout
// expired and then silently stopped, meaning the very re-render needed to
// flip the button back to enabled never happened, leaving it stuck on the
// stale disabled/"Try again in 1s" state indefinitely. Caught live via
// Playwright, not by the unit tests (which assert the countdown display
// for a single render, not this timer's own re-render behavior over real
// wall-clock time) — see DECISIONS.md. Ticking a few extra times after
// expiry (until the next real unlock attempt sets a fresh lockoutUntil or
// clears it to null) is a negligible cost, not worth a more precise
// stop condition.
setInterval(() => {
  const s = getState();
  if (s.screen !== 'landing') return;
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (profile && profile.security && profile.security.lockoutUntil) setState({});
}, 1000);
